import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { SuiteRunStatus } from '@flowqa/shared';

export type SuiteSortDirection = 'asc' | 'desc';
export type SuiteSortKey =
  | 'name'
  | 'latestRunTimestamp'
  | 'executionPassRate'
  | 'executionFrequencyPerDay'
  | 'executionFlakeScore'
  | 'executionAverageDurationMs'
  | 'executionPassRateDelta'
  | 'executionFrequencyPerDayDelta'
  | 'executionFlakeScoreDelta'
  | 'executionAverageDurationMsDelta';

export const suiteDefaultSortKey: SuiteSortKey = 'latestRunTimestamp';
export const suiteDefaultSortDirection: SuiteSortDirection = 'desc';

export const suiteSortOptions: Array<{ key: SuiteSortKey; label: string }> = [
  { key: 'name', label: 'Suite' },
  { key: 'latestRunTimestamp', label: 'Latest' },
  { key: 'executionPassRate', label: getChartTitle('passRate') },
  { key: 'executionPassRateDelta', label: 'Success Rate Delta' },
  { key: 'executionFrequencyPerDay', label: getChartTitle('frequency') },
  { key: 'executionFrequencyPerDayDelta', label: 'Frequency Delta' },
  { key: 'executionAverageDurationMs', label: getChartTitle('avgDuration') },
  { key: 'executionAverageDurationMsDelta', label: 'Avg. Duration Delta' },
  { key: 'executionFlakeScore', label: getChartTitle('flakeScore') },
  { key: 'executionFlakeScoreDelta', label: 'Flake Score Delta' },
];

export const suiteQuickSortOptions: Array<{
  key: SuiteSortKey;
  label: string;
  metric: 'passRate' | 'frequency' | 'avgDuration' | 'flakeScore';
  firstDirection?: 'asc' | 'desc';
}> = [
  { key: 'executionPassRate', label: getChartTitle('passRate'), metric: 'passRate' },
  { key: 'executionFrequencyPerDay', label: getChartTitle('frequency'), metric: 'frequency' },
  { key: 'executionAverageDurationMs', label: getChartTitle('avgDuration'), metric: 'avgDuration' },
  {
    key: 'executionFlakeScore',
    label: getChartTitle('flakeScore'),
    metric: 'flakeScore',
    firstDirection: 'asc',
  },
];

export const suiteSortFieldMap: Record<SuiteSortKey, string> = {
  name: 'name',
  latestRunTimestamp: 'latestRunTimestamp',
  executionPassRate: 'executionPassRate',
  executionFrequencyPerDay: 'executionFrequencyPerDay',
  executionFlakeScore: 'executionFlakeScore',
  executionAverageDurationMs: 'executionAverageDurationMs',
  executionPassRateDelta: 'executionPassRateDelta',
  executionFrequencyPerDayDelta: 'executionFrequencyPerDayDelta',
  executionFlakeScoreDelta: 'executionFlakeScoreDelta',
  executionAverageDurationMsDelta: 'executionAverageDurationMsDelta',
};

export const suiteColumnOptions: Array<{ field: string; label: string; mandatory?: boolean }> = [
  { field: 'name', label: 'Suite', mandatory: true },
  { field: 'latestRunStatus', label: getChartTitle('latestStatus') },
  { field: 'testLatestRunBreakdown', label: 'Latest Tests' },
  { field: 'executionStatusCounts', label: 'Runs' },
  { field: 'metricsSummary', label: getDetailLabel('metrics') },
  { field: 'actions', label: getDetailLabel('actions') },
];

export const suiteStatusOptions: Array<{ value: SuiteRunStatus; label: string }> = [
  { value: 'passed', label: 'Passed' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
  { value: 'errored', label: 'Errored' },
];

export const suiteDefaultColumnVisibilityModel: GridColumnVisibilityModel = {
  name: true,
  latestRunStatus: true,
  testLatestRunBreakdown: true,
  executionStatusCounts: true,
  metricsSummary: true,
  latestRunTimestamp: false,
  actions: true,
};
